<template>
  <teleport to="head">
    <MetaTags
      title="DNS Lookup"
      description="Find all DNS records for a domain name with this online tool. Nslookup shows A, AAAA, CNAME, TXT, MX, SPF, NS, SOA and more."
      url="https://www.nslookup.io/"
      img1024x512="https://www.nslookup.io/img/social-1024x512.png"
      img1200x630="https://www.nslookup.io/img/social-1200x630.png"
    />
    <!-- eslint-disable -->
    <component :is="'script'" type="application/ld+json">
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "url": "https://www.nslookup.io/",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://www.nslookup.io/domains/{search_term_string}/dns-records/",
          "query-input": "required name=search_term_string"
        }
      }
    </component>
    <!-- eslint-enable -->
  </teleport>
  <SearchWithHiddenContentPage :domainSearchToUrl="(domain) => `/domains/${domain}/dns-records/`">
    <template v-slot:search-explanation>
      <span data-ccpa-link="1" />
      Find all DNS records for a domain name using this online tool. For example, try
      <dns-records-link domain="wikipedia.org" class="text-blue-600 hover:text-blue-800 hover:underline" /> or
      <dns-records-link domain="www.twitter.com" class="text-blue-600 hover:text-blue-800 hover:underline" /> to view their DNS records.
    </template>
    <template v-slot:content>
      <h1 class="text-3xl mt-14">Dns lookup</h1>
      <p>Online nslookup is a web based DNS client that queries DNS records for a given domain name. It
        allows you to view all the DNS records for a website. It provides the same information as command
        line tools like dig and nslookup, from the convenience of your web browser.</p>
      <p>Nslookup.io does not cache the DNS responses it shows, but the DNS servers that are queried usually
        do respond with a cached record set. Except for authoritative DNS servers. They never serve cached
        DNS records.</p>
      <h2>How does online nslookup work?</h2>
      <p>Using nslookup online is very simple. Enter a domain name in the search bar above and hit 'enter'.
        This will take you to an overview of DNS records for the domain name you specified. Behind the
        scenes, NsLookup.io will query a DNS server for DNS records without caching the results.</p>
      <p>You can select from a range of DNS servers on the result page. These include popular public DNS
        servers, the authoritative DNS server for the given domain, and local DNS servers all around the
        world. By default, only <router-link to="/learning/dns-record-types/a/">A</router-link>,
        <router-link to="/learning/dns-record-types/aaaa/">AAAA</router-link>,
        <router-link to="/learning/dns-record-types/cname/">CNAME</router-link>,
        <router-link to="/learning/dns-record-types/txt/">TXT</router-link>,
        <router-link to="/learning/dns-record-types/ns/">NS</router-link>,
        <router-link to="/learning/dns-record-types/mx/">MX</router-link> and
        <router-link to="/learning/dns-record-types/soa/">SOA</router-link> records are shown, but you can
        select additional record types.
      </p>
      <h2>Supported DNS record types</h2>
      <p><router-link to="/learning/dns-record-types/">DNS has many record types</router-link> that each
        serve a distinct purpose. However, some of these record types are no longer in use. Wikipedia has a
        list of <a href="https://en.wikipedia.org/wiki/List_of_DNS_record_types#Obsolete_record_types" target="_blank" rel="noopener noreferrer">obsolete record types</a>.
        These obsolete types are not supported by nslookup.io. All the DNS record types that are known to be
        in active use are supported by nslookup:</p>
      <ul class="ml-2 list-disc list-inside">
        <li v-for="(otherRecordType, index) in recordTypes" :key="index">
          <router-link :to="`/${otherRecordType.lower}-lookup/`">{{ otherRecordType.upper }} lookup</router-link> &mdash; {{ otherRecordType.description }}
        </li>
      </ul>
      <h2>Use cases for online DNS lookup</h2>
      <p>There are many situations where online nslookup can be a useful tool. For example, when you are
        configuring the DNS records of your own domain, you might want to check whether you have configured
        them correctly. You can do this by entering the domain name at the top of this page.</p>
      <p>Whenever you change DNS records, it takes a while for it to propagate through the entire domain name
        system. This takes time because DNS servers cache records in order to speed up DNS resolution. So
        whenever you change DNS records, you can check whether they have propagated yet by doing a DNS
        lookup.</p>
      <p>There are of course more use cases for online nslookup, instead of using a CLI. Those include being
        able to share the results with a colleague or friend, and the ability to see multiple record types at
        the same time, without querying multiple times.</p>
    </template>
  </SearchWithHiddenContentPage>
</template>

<script setup lang="ts">
import { recordTypes } from '@/common/recordTypes';
import DnsRecordsLink from '@/components/micro/DnsRecordsLink.vue';
import MetaTags from '@/components/MetaTags.vue';
import SearchWithHiddenContentPage from '@/components/pages/SearchWithHiddenContentPage.vue';
</script>
